import {
  Box,
  CssBaseline,
  Tab,
  Tabs,
  ThemeProvider,
  Typography,
} from '@mui/material';
import useAppTheme from 'hooks/useAppTheme';
import * as React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { darkTheme, lightTheme } from './theme/MuiTheme';
import { WorkOrdersCreated } from './WordOrdersCreated/WorkOrdersCreated';
import { WorkOrderRequests } from './WorkOrdersRequest/WorkOrdersRequest';
import type { ServiceView } from 'types/survivalAnalysis.types';
import type { ComponentDetails } from 'types/squares.type';
import { useTranslation } from 'react-i18next';

function a11yProps(index: number) {
  return {
    id: `simple-tab-${index}`,
    'aria-controls': `simple-tabpanel-${index}`,
  };
}

interface CustomTabPanelProps {
  children?: React.ReactNode;
  value: number;
  index: number;
  [key: string]: any;
}

const CustomTabPanel = ({
  children,
  value,
  index,
  ...other
}: CustomTabPanelProps) => {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box
          py={'12px'}
          sx={{
            height: 'calc(100vh - 140px)',
          }}
        >
          {children}
        </Box>
      )}
    </div>
  );
};

const tabsConfig = (
  props: {
    components: any[];
    asset: any;
    clearStateDefaultState: () => void;
  },
  t: (key: string) => string
) => [
  {
    label: t('work_orders_created'),
    component: <WorkOrdersCreated {...props} />,
  },
  {
    label: t('work_order_requests'),
    component: <WorkOrderRequests {...props} />,
  },
];

export const WorkOrderManagement = () => {
  const { t } = useTranslation();
  const { enableDarkTheme } = useAppTheme();
  const [value, setValue] = React.useState(0);
  const [routeState, setRouteState] = React.useState<{
    components: ComponentDetails[];
    asset: ServiceView | null;
  }>({
    components: [],
    asset: null,
  });

  const { state } = useLocation();
  const navigate = useNavigate();

  function clearStateDefaultState() {
    setRouteState({
      components: [],
      asset: null,
    });
  }

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
  };

  React.useEffect(() => {
    if (state) {
      setRouteState(state);

      navigate(location.pathname, {
        replace: true,
        state: null,
      });
    }
  }, [state, navigate, location.pathname]);

  const { components, asset } = routeState;
  const tabs = tabsConfig({ components, asset, clearStateDefaultState }, t);

  return (
    <ThemeProvider theme={enableDarkTheme ? darkTheme : lightTheme}>
      <CssBaseline />
      <Box sx={{ width: '100%', paddingTop: '8px', gap: '16px' }}>
        <Tabs
          value={value}
          onChange={handleChange}
          aria-label={t('work_management_tabs')}
          sx={{
            borderBottom: 1,
            borderColor: 'divider',
            minHeight: '32px',
            height: '36px',
            gap: '16px',
          }}
        >
          {tabs.map((tab, index) => (
            <Tab
              key={index}
              label={
                <Typography variant="h5" fontSize="14px" textTransform="none">
                  {tab.label}
                </Typography>
              }
              {...a11yProps(index)}
              sx={{
                minHeight: '32px',
                height: '32px',
                padding: '8px 16px',
              }}
            />
          ))}
        </Tabs>

        {tabs.map((tab, index) => (
          <CustomTabPanel key={index} value={value} index={index}>
            {tab.component}
          </CustomTabPanel>
        ))}
      </Box>
    </ThemeProvider>
  );
};
